import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Save, ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const F = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);
const I = (p) => <input {...p} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />;
const TA = (p) => <textarea {...p} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />;

const FIELD_TYPES = ["text", "email", "tel", "number", "textarea", "select", "checkbox"];

const FormEditor = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get(`${API}/cms/forms/${id}`).then((r) => setForm(r.data)).catch(() => toast.error("Form not found")); }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/forms/${id}`, form);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  if (!form) return <div className="text-slate-400">Loading…</div>;

  const patch = (k, v) => setForm({ ...form, [k]: v });
  const updateField = (i, patch) => { const next = [...form.fields]; next[i] = { ...next[i], ...patch }; setForm({ ...form, fields: next }); };
  const addField = () => setForm({ ...form, fields: [...form.fields, { key: `field_${form.fields.length + 1}`, label: "New Field", type: "text", required: false, options: [] }] });
  const removeField = (i) => setForm({ ...form, fields: form.fields.filter((_, j) => j !== i) });
  const moveField = (i, d) => { const next = [...form.fields]; const j = i + d; if (j < 0 || j >= next.length) return; [next[i], next[j]] = [next[j], next[i]]; setForm({ ...form, fields: next }); };
  const copyEmbed = () => { navigator.clipboard.writeText(`{{form:${form.slug}}}`); toast.success("Embed token copied. Paste into Rich Text section."); };
  const publicUrl = `${BASE}/api/public/form/${form.slug}`;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/app/website/forms" className="text-slate-500 hover:text-slate-900 p-1"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{form.name}</h1>
            <p className="text-xs text-slate-500">Slug: <code>{form.slug}</code></p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={copyEmbed} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg text-sm"><Copy size={14} /> Copy embed token</button>
          <Link to={`/app/website/forms/${id}/submissions`} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg text-sm">View submissions</Link>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-prepify-600 text-white font-medium px-5 py-2.5 rounded-lg disabled:opacity-50" data-testid="save-form-btn">
            <Save size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-semibold">Basics</h3>
            <F label="Name"><I value={form.name} onChange={(e) => patch("name", e.target.value)} /></F>
            <F label="Description"><TA rows={2} value={form.description || ""} onChange={(e) => patch("description", e.target.value)} /></F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Submit button label"><I value={form.submit_label} onChange={(e) => patch("submit_label", e.target.value)} /></F>
              <F label="Success message"><I value={form.success_message} onChange={(e) => patch("success_message", e.target.value)} /></F>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Fields</h3>
              <button onClick={addField} className="text-xs bg-prepify-600 hover:bg-prepify-700 text-white px-3 py-1.5 rounded inline-flex items-center gap-1"><Plus size={12} /> Add field</button>
            </div>
            <div className="space-y-3">
              {form.fields.map((f, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-4 bg-slate-50/40 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono bg-white border border-slate-200 rounded px-2 py-1">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex-1"></div>
                    <button onClick={() => moveField(i, -1)} disabled={i === 0} className="p-1 hover:bg-white rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                    <button onClick={() => moveField(i, 1)} disabled={i === form.fields.length - 1} className="p-1 hover:bg-white rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                    <button onClick={() => removeField(i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Field key (machine name)"><I value={f.key} onChange={(e) => updateField(i, { key: e.target.value })} /></F>
                    <F label="Label"><I value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} /></F>
                    <F label="Type">
                      <select value={f.type} onChange={(e) => updateField(i, { type: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                        {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </F>
                    <F label="Placeholder"><I value={f.placeholder || ""} onChange={(e) => updateField(i, { placeholder: e.target.value })} /></F>
                    <F label="Help text (shown below field)"><I value={f.help_text || ""} onChange={(e) => updateField(i, { help_text: e.target.value })} /></F>
                    <label className="flex items-center gap-2 pt-6"><input type="checkbox" checked={f.required} onChange={(e) => updateField(i, { required: e.target.checked })} /> <span className="text-sm">Required</span></label>
                  </div>
                  {f.type === "select" && (
                    <F label="Options (one per line)">
                      <TA rows={3} value={(f.options || []).join("\n")} onChange={(e) => updateField(i, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} />
                    </F>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h3 className="text-sm font-semibold">Settings</h3>
            <F label="Notification email (on new submission)"><I type="email" value={form.notification_email || ""} onChange={(e) => patch("notification_email", e.target.value)} /></F>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.create_lead} onChange={(e) => patch("create_lead", e.target.checked)} /> Submissions become CRM Leads</label>
          </section>
          <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-2">
            <h3 className="text-sm font-semibold">Endpoint</h3>
            <p className="text-xs text-slate-500">Public fetch URL:</p>
            <code className="block text-xs bg-slate-50 border border-slate-200 rounded p-2 break-all">GET {publicUrl}</code>
            <p className="text-xs text-slate-500">Submit URL:</p>
            <code className="block text-xs bg-slate-50 border border-slate-200 rounded p-2 break-all">POST {publicUrl}/submit</code>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default FormEditor;
